import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useSnackbar } from 'notistack';

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { CardHeader } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import MenuItem from '@material-ui/core/MenuItem';
import FormLabel from '@material-ui/core/FormLabel';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';

import AutoComplateProduct from '../AutoCompleteProduct/AutoCompleteProduct';
import SortableList from './SortabletListProducts/SortabletListProducts';
import IconOrder from '../../../assets/icons/IconOrder.svg';
import logo from '../../../assets/img/10x_logo.svg';
import { ContainerActions, ContainerSearch, ContainerSort, ContainerImg, ImgLoading } from './productoscollection.styles';
import { updateCollectionsService, collectionsxIdService } from '../../../services/collections';
import {
    SORT_SELECT_OPTIONS,
    getSortSelectIdFromCollection,
    getSortConfigFromSelectId,
    sortProductsBySelectId,
    buildCollectionUpdatePayload,
    applySavedSortToProducts,
    persistCollectionSortPreference,
    enrichCollectionWithSortPreference,
} from '../collectionProductSort';

const ProductsCollection = ({
    onSortEnd,
    listDataProduct,
    setListDataProduct,
    collectionData,
    collectionId,
    storeId,
    setDataCollection,
}) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const isTabletOrMobile = useMediaQuery({ query: '(max-width: 600px)' });

    const [products, setProducts] = useState([]);
    const [sortSelect, setSortSelect] = useState(() => getSortSelectIdFromCollection(collectionData));
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setSortSelect(getSortSelectIdFromCollection(collectionData));
    }, [collectionData?.sort_by, collectionData?.sort_order, collectionData?.sortBy, collectionData?.sortOrder]);

    useEffect(() => {
        setProducts(applySavedSortToProducts(listDataProduct, collectionData));
    }, [
        listDataProduct,
        collectionData?.sort_by,
        collectionData?.sort_order,
        collectionData?.sortBy,
        collectionData?.sortOrder,
    ]);

    const syncCollectionFromApi = useCallback(async () => {
        if (!collectionId || !setDataCollection) return null;
        const { data } = await collectionsxIdService(storeId, collectionId);
        const enriched = enrichCollectionWithSortPreference(data, storeId);
        setDataCollection(enriched);
        return enriched;
    }, [storeId, collectionId, setDataCollection]);

    const persistCollectionProducts = useCallback(
        (orderedList) => {
            if (!collectionId || !collectionData?.id) return Promise.resolve();
            return updateCollectionsService(
                storeId,
                buildCollectionUpdatePayload(
                    collectionData,
                    orderedList.map((p) => String(p.id))
                )
            ).then(() => syncCollectionFromApi());
        },
        [storeId, collectionId, collectionData, syncCollectionFromApi]
    );

    const handleDelete = (index) => {
        if (collectionData && !collectionData.default) {
            const items = [...listDataProduct];
            items.splice(index, 1);
            const next = applySavedSortToProducts(items, collectionData);
            setListDataProduct(next);
            setProducts(next);
            persistCollectionProducts(next).catch(() => {
                enqueueSnackbar('Error al quitar el producto de la categoría', {
                    variant: 'error',
                    autoHideDuration: 2500,
                });
            });
        }
    };

    const handleSelectChange = async ({ target }) => {
        const raw = target.value;
        setLoading(true);

        try {
            if (raw === '' || raw === null) {
                setSortSelect(null);
                setProducts(listDataProduct);
                if (!collectionId || !collectionData?.id) return;
                await updateCollectionsService(
                    storeId,
                    buildCollectionUpdatePayload(collectionData, listDataProduct.map((p) => p.id), {
                        sort_by: 'manual',
                        sort_order: 'asc',
                    })
                );
                await syncCollectionFromApi();
                persistCollectionSortPreference(storeId, collectionId, 'manual', 'asc');
                enqueueSnackbar('Orden manual activado', { variant: 'success', autoHideDuration: 2500 });
                return;
            }

            const selectId = Number(raw);
            setSortSelect(selectId);
            const sorted = sortProductsBySelectId(listDataProduct, selectId);
            setProducts(sorted);
            setListDataProduct(sorted);

            if (!collectionId || !collectionData?.id) {
                return;
            }

            const { sort_by, sort_order } = getSortConfigFromSelectId(selectId);
            await updateCollectionsService(
                storeId,
                buildCollectionUpdatePayload(collectionData, sorted.map((p) => p.id), {
                    sort_by,
                    sort_order,
                })
            );

            await syncCollectionFromApi();
            persistCollectionSortPreference(storeId, collectionId, sort_by, sort_order);

            enqueueSnackbar('Orden guardado correctamente', {
                variant: 'success',
                autoHideDuration: 2500,
            });
        } catch (err) {
            enqueueSnackbar('Error al guardar el orden', {
                variant: 'error',
                autoHideDuration: 2500,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card
            style={{
                marginTop: isTabletOrMobile ? 16 : 15,
                marginBottom: isTabletOrMobile ? 16 : 0,
                ...(isTabletOrMobile
                    ? {
                          borderRadius: 12,
                          border: '1px solid #E8E8E8',
                          boxShadow: '0px 1px 3px rgba(0,0,0,0.06)',
                      }
                    : {}),
            }}
            elevation={0}
        >
            <CardHeader
                titleTypographyProps={{
                    variant: 'h6',
                    style: isTabletOrMobile ? { fontSize: 16, fontWeight: 600 } : undefined,
                }}
                title={isTabletOrMobile ? 'Productos en esta categoría' : t('ProductsTitle')}
            />
            {!isTabletOrMobile && (
                <div style={{ paddingLeft: 15 }}>
                    <Typography component="span" variant="body1" color="textPrimary">
                        {t('Collection.productDescriptionAdd')}
                    </Typography>
                </div>
            )}
            <CardContent style={isTabletOrMobile ? { paddingLeft: 16, paddingRight: 16, paddingBottom: 16 } : undefined}>
                <ContainerActions style={isTabletOrMobile ? { flexDirection: 'column', alignItems: 'stretch', gap: 12 } : {}}>
                    <ContainerSearch style={isTabletOrMobile ? { width: '100%' } : {}}>
                        <AutoComplateProduct
                            collectionId={collectionId}
                            collectionData={collectionData}
                            listDataProduct={listDataProduct}
                            setListDataProduct={setListDataProduct}
                        />
                    </ContainerSearch>
                    <ContainerSort style={isTabletOrMobile ? { width: '100%' } : {}}>
                        <FormLabel style={{ margin: '8px' }}>
                            <img style={{ marginRight: 5 }} src={IconOrder} alt="" />
                            {t('Product.filter.order')}
                        </FormLabel>
                        <FormControl variant="outlined" style={{ paddingTop: '5px' }}>
                            <Select
                                labelId="demo-simple-select-outlined-label"
                                id="demo-simple-select-outlined"
                                value={sortSelect ?? ''}
                                onChange={handleSelectChange}
                                displayEmpty
                                disabled={products.length <= 1 || loading}
                            >
                                <MenuItem value="">
                                    <em>Orden manual (arrastrar)</em>
                                </MenuItem>
                                {SORT_SELECT_OPTIONS.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        {option.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </ContainerSort>
                </ContainerActions>
                {loading ? (
                    <ContainerImg>
                        <ImgLoading src={logo} alt="logo" />
                        <Typography>Ordenando tus productos...</Typography>
                    </ContainerImg>
                ) : (
                    <SortableList
                        pressDelay={100}
                        useDragHandle
                        items={products}
                        handleClick={handleDelete}
                        onSortEnd={onSortEnd}
                        setLoading={setLoading}
                        sortSelect={sortSelect}
                    />
                )}
            </CardContent>
        </Card>
    );
};

const mapStateToProps = ({ auth }) => ({
    storeId: auth.storeId,
});

export default connect(mapStateToProps)(ProductsCollection);
